import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  EmploymentTemplateListItem,
  EmploymentTemplateSalary,
} from "services/api/company/staff.service";
import { useCompanySubscription } from "web-apps/company/components/common/hooks/useCompanySubscription";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import { getAgeFromPersonalIdentityNumber } from "web-apps/company/utils/personalIdentityNumber.utils";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import type {
  AddStaffFormData,
  AddStaffOption,
  AddStaffValidationRequirements,
  AddStaffValidationState,
} from "./addStaffPanel.contract";
import {
  createInitialFormData,
  isAddStaffFormStarted,
  mapAddStaffFormDataToWorkerPayload,
} from "./addStaffPanel.mapper";
import {
  getSalaryAmountLabel,
  getAddStaffOptions,
} from "./addStaffPanel.options.logic";
import {
  createWorkerRequest,
  fetchEmployeeNumberRequest,
  fetchEmploymentTemplateDetailRequest,
  fetchEmploymentTemplatesRequest,
  lookupWorkerByPhoneRequest,
  type PhoneLookupState,
  type SubmitState,
} from "./addStaffPanel.request.logic";
import { getAddStaffFieldErrors } from "./addStaffPanel.validation.logic";
import {
  getAddStaffValidationRequirements,
  getAddStaffValidationState,
} from "./addStaffPanel.validator";
import { filterTemplateSalariesByAge } from "../shared/staffEmploymentTemplate.logic";
import {
  toEmploymentUiRules,
  withEmploymentTypeChangePrefill,
  withNormalizedEmploymentFormByRules,
} from "../shared/staffEmploymentRules.logic";
import { hasValidPhoneNumber } from "../shared/staffInputValidation.logic";

interface UseAddStaffPanelLogicProps {
  readonly isOpen: boolean;
  readonly t: TFunction;
  readonly unitTeamOptions: UnitTeamOption[];
  readonly onClose: () => void;
  readonly onWorkerCreated?: () => void;
}

interface AddStaffPanelLogicResult {
  readonly agreementError: string;
  readonly areFormFieldsDisabled: boolean;
  readonly emailError: string;
  readonly employeeCategoryOptions: AddStaffOption[];
  readonly employeeNumberError: string;
  readonly employmentOptions: AddStaffOption[];
  readonly employmentTemplates: EmploymentTemplateListItem[];
  readonly firstNameError: string;
  readonly formData: AddStaffFormData;
  readonly isCheckingPhoneNumber: boolean;
  readonly isCreateDisabled: boolean;
  readonly isFormStarted: boolean;
  readonly isLoadingEmployeeNumber: boolean;
  readonly isLoadingEmploymentTemplates: boolean;
  readonly isSubmitting: boolean;
  readonly lastNameError: string;
  readonly noticeDaysError: string;
  readonly paymentMethodError: string;
  readonly paymentMethodOptions: AddStaffOption[];
  readonly paymentOptions: AddStaffOption[];
  readonly personalIdentityNumberError: string;
  readonly phoneError: string;
  readonly roleError: string;
  readonly salaryAmountLabel: string;
  readonly salaryOptions: AddStaffOption[];
  readonly selectedEmploymentTemplateId: string;
  readonly selectedTemplateSalaryId: string;
  readonly submitError: string;
  readonly teamOptions: AddStaffOption[];
  readonly templateSalaries: EmploymentTemplateSalary[];
  readonly vacationDaysError: string;
  readonly validationRequirements: AddStaffValidationRequirements;
  readonly validationState: AddStaffValidationState;
  readonly workPercentageError: string;
  readonly workplaceError: string;
  readonly applyEmploymentTemplate: (templateId: string) => void;
  readonly applyEmploymentTemplateSalary: (salaryId: string) => void;
  readonly fetchEmployeeNumber: () => void;
  readonly onAddWorkerClick: () => void;
  readonly onPhoneNumberBlur: () => void;
  readonly onPhoneNumberChange: (phoneNumber: string) => void;
  readonly onPhoneNumberContinue: () => void;
  readonly setField: <K extends keyof AddStaffFormData>(
    field: K,
    value: AddStaffFormData[K],
  ) => void;
}

const MAX_PHONE_DIGITS = 15;

export const useAddStaffPanelLogic = (
  props: UseAddStaffPanelLogicProps,
): AddStaffPanelLogicResult => {
  const isOpen = props.isOpen;
  const onClose = props.onClose;
  const onWorkerCreated = props.onWorkerCreated;
  const translate = props.t;
  const unitTeamOptions = props.unitTeamOptions;
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;
  const initialFormDataRef = useRef<AddStaffFormData | null>(null);
  const [formData, setFormData] = useState<AddStaffFormData>(() => {
    const initialFormData = createInitialFormData();
    initialFormDataRef.current = initialFormData;
    return initialFormData;
  });
  const [isLoadingEmployeeNumber, setIsLoadingEmployeeNumber] = useState(false);
  const [employmentTemplates, setEmploymentTemplates] = useState<
    EmploymentTemplateListItem[]
  >([]);
  const [isLoadingEmploymentTemplates, setIsLoadingEmploymentTemplates] =
    useState(false);
  const [selectedEmploymentTemplateId, setSelectedEmploymentTemplateId] =
    useState("");
  const [templateSalaries, setTemplateSalaries] = useState<
    EmploymentTemplateSalary[]
  >([]);
  const [selectedTemplateSalaryId, setSelectedTemplateSalaryId] = useState("");
  const [phoneLookupState, setPhoneLookupState] = useState<PhoneLookupState>({
    status: "idle",
  });
  const [submitState, setSubmitState] = useState<SubmitState>({
    status: "idle",
  });
  const [submitError, setSubmitError] = useState("");
  const isMountedRef = useRef(true);
  const employeeNumberRequestIdRef = useRef(0);
  const createWorkerRequestIdRef = useRef(0);
  const phoneLookupRequestIdRef = useRef(0);
  const employmentTemplatesRequestIdRef = useRef(0);
  const employmentTemplateDetailRequestIdRef = useRef(0);
  const companyAddressRef = useRef(companyState.company?.address);
  companyAddressRef.current = companyState.company?.address;

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const setField = useCallback(
    <K extends keyof AddStaffFormData>(
      field: K,
      value: AddStaffFormData[K],
    ): void => {
      setFormData((previousFormData) =>
        withEmploymentTypeChangePrefill(
          { ...previousFormData, [field]: value },
          field,
          value,
          companyAddressRef.current,
        ),
      );
      setSubmitError("");
    },
    [],
  );

  const fetchEmployeeNumber = useCallback((): void => {
    if (!companyId) return;
    const requestId = employeeNumberRequestIdRef.current + 1;
    employeeNumberRequestIdRef.current = requestId;
    setIsLoadingEmployeeNumber(true);
    void fetchEmployeeNumberRequest({
      companyId,
      requestId,
      getCurrentRequestId: () => employeeNumberRequestIdRef.current,
      isMountedRef,
      setField,
      setIsLoadingEmployeeNumber,
    });
  }, [companyId, setField]);

  const fetchEmploymentTemplates = useCallback((): void => {
    if (!companyId) return;
    const requestId = employmentTemplatesRequestIdRef.current + 1;
    employmentTemplatesRequestIdRef.current = requestId;
    setIsLoadingEmploymentTemplates(true);
    void fetchEmploymentTemplatesRequest({
      companyId,
      requestId,
      getCurrentRequestId: () => employmentTemplatesRequestIdRef.current,
      isMountedRef,
      setEmploymentTemplates,
      setIsLoadingEmploymentTemplates,
    });
  }, [companyId]);

  const onPhoneNumberChange = useCallback(
    (phoneNumber: string): void => {
      phoneLookupRequestIdRef.current += 1;
      setPhoneLookupState({ status: "idle" });
      setSubmitState({ status: "idle" });
      setSubmitError("");
      setField(
        "phoneNumber",
        phoneNumber.replace(/\D/g, "").slice(0, MAX_PHONE_DIGITS),
      );
    },
    [setField],
  );

  const onPhoneNumberBlur = useCallback((): void => {
    if (!hasValidPhoneNumber({ value: formData.phoneNumber })) {
      if (formData.phoneNumber.trim().length === 0) {
        setPhoneLookupState({ status: "idle" });
        return;
      }
      setPhoneLookupState({
        status: "invalid",
        message: translate("Staff.AddStaffPanelPhoneNumberInvalid"),
      });
      return;
    }
    if (phoneLookupState.status === "invalid") {
      setPhoneLookupState({ status: "idle" });
    }
  }, [formData.phoneNumber, phoneLookupState.status, translate]);

  const onPhoneNumberContinue = useCallback((): void => {
    if (!companyId) return;
    if (!hasValidPhoneNumber({ value: formData.phoneNumber })) {
      if (formData.phoneNumber.trim().length > 0) {
        setPhoneLookupState({
          status: "invalid",
          message: translate("Staff.AddStaffPanelPhoneNumberInvalid"),
        });
      } else {
        setPhoneLookupState({ status: "idle" });
      }
      return;
    }
    setSubmitState({ status: "idle" });
    setSubmitError("");
    setPhoneLookupState({ status: "checking" });
    const requestId = phoneLookupRequestIdRef.current + 1;
    phoneLookupRequestIdRef.current = requestId;
    void lookupWorkerByPhoneRequest({
      companyId,
      phoneNumber: formData.phoneNumber,
      requestId,
      getCurrentRequestId: () => phoneLookupRequestIdRef.current,
      isMountedRef,
      setFormData,
      setPhoneLookupState,
      translate,
    });
  }, [companyId, formData.phoneNumber, translate]);

  const applyEmploymentTemplate = useCallback(
    (templateId: string): void => {
      setSelectedEmploymentTemplateId(templateId);
      setSelectedTemplateSalaryId("");
      setTemplateSalaries([]);
      if (!companyId || templateId.length === 0) return;
      const requestId = employmentTemplateDetailRequestIdRef.current + 1;
      employmentTemplateDetailRequestIdRef.current = requestId;
      void fetchEmploymentTemplateDetailRequest({
        companyId,
        employmentTemplateId: Number(templateId),
        requestId,
        getCurrentRequestId: () => employmentTemplateDetailRequestIdRef.current,
        isMountedRef,
        setFormData,
        setTemplateSalaries,
      });
    },
    [companyId],
  );

  const applyEmploymentTemplateSalary = useCallback(
    (salaryId: string): void => {
      setSelectedTemplateSalaryId(salaryId);
      const selectedSalary = templateSalaries.find(
        (salary) => String(salary.id) === salaryId,
      );
      if (selectedSalary == null) return;
      setField("salaryAmount", String(selectedSalary.value));
    },
    [setField, templateSalaries],
  );

  useEffect(() => {
    createWorkerRequestIdRef.current += 1;
    employeeNumberRequestIdRef.current += 1;
    phoneLookupRequestIdRef.current += 1;
    employmentTemplatesRequestIdRef.current += 1;
    employmentTemplateDetailRequestIdRef.current += 1;
    if (!isOpen) return;

    const initialFormData = createInitialFormData();
    initialFormDataRef.current = initialFormData;
    setFormData(initialFormData);
    setPhoneLookupState({ status: "idle" });
    setSubmitState({ status: "idle" });
    setSubmitError("");
    setSelectedEmploymentTemplateId("");
    setSelectedTemplateSalaryId("");
    setTemplateSalaries([]);
    fetchEmployeeNumber();
    fetchEmploymentTemplates();
  }, [fetchEmployeeNumber, fetchEmploymentTemplates, isOpen]);

  const subscription = useCompanySubscription({
    companyId,
    isEnabled: isOpen,
  });
  const employmentRules = useMemo(() => {
    return toEmploymentUiRules(
      formData.employmentType ?? "",
      formData.salaryType ?? "",
      subscription,
      companyState.company?.companySettings ?? [],
    );
  }, [
    companyState.company?.companySettings,
    formData.employmentType,
    formData.salaryType,
    subscription,
  ]);

  useEffect(() => {
    setFormData((previousFormData) => {
      const current = {
        salaryType: previousFormData.salaryType ?? "",
        employeeCategory: previousFormData.employeeCategory,
        paymentType: previousFormData.paymentType,
      };
      const normalized = withNormalizedEmploymentFormByRules(
        current,
        employmentRules,
      );
      return normalized.salaryType === current.salaryType &&
        normalized.employeeCategory === current.employeeCategory &&
        normalized.paymentType === current.paymentType
        ? previousFormData
        : { ...previousFormData, ...normalized };
    });
  }, [employmentRules]);

  const options = useMemo(() => {
    return getAddStaffOptions({
      employmentRules,
      translate,
      unitTeamOptions,
    });
  }, [employmentRules, translate, unitTeamOptions]);
  const workerAge = useMemo(() => {
    return getAgeFromPersonalIdentityNumber(formData.personalIdentityNumber);
  }, [formData.personalIdentityNumber]);
  const visibleTemplateSalaries = useMemo(() => {
    return filterTemplateSalariesByAge(templateSalaries, workerAge);
  }, [templateSalaries, workerAge]);
  const validationRequirements = useMemo(() => {
    return getAddStaffValidationRequirements(formData);
  }, [formData]);
  const validationState = useMemo(() => {
    return getAddStaffValidationState({
      canSelectSimflexityPay: employmentRules.canSelectSimflexityPay,
      formData,
      requirements: validationRequirements,
    });
  }, [
    employmentRules.canSelectSimflexityPay,
    formData,
    validationRequirements,
  ]);
  const fieldErrors = useMemo(() => {
    return getAddStaffFieldErrors({
      formData,
      translate,
      validationState,
    });
  }, [formData, translate, validationState]);
  const salaryAmountLabel = useMemo(() => {
    return getSalaryAmountLabel({
      salaryType: formData.salaryType,
      translate,
    });
  }, [formData.salaryType, translate]);
  const isSubmitting = submitState.status === "submitting";
  const hasCheckedPhoneNumber = phoneLookupState.status === "verified";
  const phoneError =
    phoneLookupState.status === "invalid" ||
    phoneLookupState.status === "alreadyExists"
      ? phoneLookupState.message
      : "";
  const areFormFieldsDisabled =
    formData.phoneNumber.trim().length === 0 || !hasCheckedPhoneNumber;
  const isCreateDisabled =
    areFormFieldsDisabled ||
    isSubmitting ||
    !validationState.hasRequiredPersonalValues ||
    !validationState.hasRequiredEmploymentValues;
  const isFormStarted = useMemo(() => {
    return (
      initialFormDataRef.current != null &&
      isAddStaffFormStarted(formData, initialFormDataRef.current)
    );
  }, [formData]);

  const onAddWorkerClick = useCallback((): void => {
    if (!companyId || isSubmitting) return;
    if (!validationState.hasRequiredPersonalValues) return;
    if (!validationState.hasRequiredEmploymentValues) return;
    if (!hasValidPhoneNumber({ value: formData.phoneNumber })) {
      setPhoneLookupState({
        status: "invalid",
        message: translate("Staff.AddStaffPanelPhoneNumberInvalid"),
      });
      return;
    }
    setSubmitError("");
    setSubmitState({ status: "submitting" });
    const requestId = createWorkerRequestIdRef.current + 1;
    createWorkerRequestIdRef.current = requestId;
    void createWorkerRequest({
      companyId,
      formData,
      requestId,
      payload: mapAddStaffFormDataToWorkerPayload(formData),
      getCurrentRequestId: () => createWorkerRequestIdRef.current,
      isMountedRef,
      onWorkerCreated,
      onClose,
      setSubmitError,
      setSubmitState,
      translate,
    });
  }, [
    companyId,
    formData,
    isSubmitting,
    onClose,
    onWorkerCreated,
    translate,
    validationState.hasRequiredEmploymentValues,
    validationState.hasRequiredPersonalValues,
  ]);

  return {
    agreementError: fieldErrors.agreement,
    areFormFieldsDisabled,
    emailError: fieldErrors.email,
    employeeCategoryOptions: options.employeeCategoryOptions,
    employeeNumberError: fieldErrors.employeeNumber,
    employmentOptions: options.employmentOptions,
    employmentTemplates,
    firstNameError: fieldErrors.firstName,
    formData,
    isCheckingPhoneNumber: phoneLookupState.status === "checking",
    isCreateDisabled,
    isFormStarted,
    isLoadingEmployeeNumber,
    isLoadingEmploymentTemplates,
    isSubmitting,
    lastNameError: fieldErrors.lastName,
    noticeDaysError: fieldErrors.noticeDays,
    paymentMethodError: fieldErrors.paymentMethod,
    paymentMethodOptions: options.paymentMethodOptions,
    paymentOptions: options.paymentOptions,
    personalIdentityNumberError: fieldErrors.personalIdentityNumber,
    phoneError,
    roleError: fieldErrors.role,
    salaryAmountLabel,
    salaryOptions: options.salaryOptions,
    selectedEmploymentTemplateId,
    selectedTemplateSalaryId,
    submitError,
    teamOptions: options.teamOptions,
    templateSalaries: visibleTemplateSalaries,
    vacationDaysError: fieldErrors.vacationDays,
    validationRequirements,
    validationState,
    workPercentageError: fieldErrors.workPercentage,
    workplaceError: fieldErrors.workplace,
    applyEmploymentTemplate,
    applyEmploymentTemplateSalary,
    fetchEmployeeNumber,
    onAddWorkerClick,
    onPhoneNumberBlur,
    onPhoneNumberChange,
    onPhoneNumberContinue,
    setField,
  };
};
